import { IconTrash } from "@tabler/icons-react";
import React from "react";
import { toast } from "sonner";

import { Loader } from "@/components/Loader";
import { useTeam } from "@/components/providers/team";
import { useFetchTeamMembers } from "@/components/queries/team";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";

export function RemoveMemberDialog({
	memberID,
	disabled,
}: {
	memberID: string;
	disabled: boolean;
}) {
	const { teamID } = useTeam();
	const [isLoading, setIsLoading] = React.useState(false);
	const { refetchTeamMembers } = useFetchTeamMembers(teamID as string);

	return (
		<AlertDialog>
			<AlertDialogTrigger
				className={buttonVariants({
					variant: "destructive",
					size: "icon-sm",
				})}
				aria-label="Remove team member"
				disabled={disabled}
			>
				<IconTrash className="h-4 w-4" />
			</AlertDialogTrigger>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>Remove Team Member</AlertDialogTitle>
					<AlertDialogDescription>
						Are you sure you want to remove this member from the team? This
						action cannot be undone.
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel>Cancel</AlertDialogCancel>
					<AlertDialogAction
						onClick={async () => {
							if (disabled) return;
							setIsLoading(true);
							try {
								const response = await requestWithHandling<void>(() =>
									apiClient.api.teamMemberDelete(teamID as string, {
										id: memberID,
									}),
								);
								if (response.code === "SUCCESS") {
									toast.success("Team member removed successfully");
									await refetchTeamMembers();
								} else {
									throw new Error(
										response.message || "Failed to remove team member",
									);
								}
							} catch (error) {
								toast.error("Failed to remove team member", {
									description:
										error instanceof TypedClientApiError
											? error.message
											: "An unknown error occurred",
								});
							} finally {
								setIsLoading(false);
							}
						}}
						disabled={isLoading || disabled}
						className="bg-destructive hover:bg-destructive/90"
					>
						{isLoading ? <Loader /> : "Remove"}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
